import { useState } from 'react'
import { Check, Loader2, Workflow } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useAssignNumberToFlow, useIvrFlows } from '@/api/ivrFlows'

interface Props {
  open: boolean
  onClose: () => void
  phoneNumberId: number
  /** Flow actuellement assigné (pour le présélectionner). */
  currentFlowId: number | null
}

export function SelectFlowDialog({
  open,
  onClose,
  phoneNumberId,
  currentFlowId,
}: Props) {
  const { t } = useTranslation()
  const { data: flows = [], isLoading } = useIvrFlows()
  const assignFlow = useAssignNumberToFlow()

  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(
    currentFlowId,
  )

  const handleConfirm = async () => {
    if (!selectedFlowId) return
    try {
      await assignFlow.mutateAsync({
        flowId: selectedFlowId,
        phoneNumberId,
      })
      toast.success(t('manager.numbers.flowAssigned'))
      onClose()
    } catch {
      toast.error(t('manager.numbers.flowAssignError'))
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && !assignFlow.isPending) onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('manager.numbers.selectFlowTitle')}</DialogTitle>
          <DialogDescription>
            {t('manager.numbers.selectFlowDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : flows.length === 0 ? (
            <div className="text-center py-8">
              <Workflow className="h-10 w-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('manager.numbers.noFlows')}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {flows.map((flow) => {
                const isSelected = selectedFlowId === flow.id
                return (
                  <button
                    key={flow.id}
                    type="button"
                    onClick={() => setSelectedFlowId(flow.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors border',
                      isSelected
                        ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-500/50 dark:bg-indigo-500/10'
                        : 'border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50',
                    )}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                        isSelected
                          ? 'bg-indigo-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500',
                      )}
                    >
                      <Workflow className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {flow.name}
                        </p>
                        {!flow.is_active && (
                          <Badge variant="outline" className="text-[10px]">
                            {t('manager.numbers.flowInactive')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('manager.numbers.flowMeta', {
                          nodes: flow.node_count,
                          numbers: flow.assigned_numbers_count,
                        })}
                      </p>
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={assignFlow.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              !selectedFlowId ||
              selectedFlowId === currentFlowId ||
              assignFlow.isPending
            }
            className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
          >
            {assignFlow.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {t('manager.numbers.assignFlow')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
